import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, CircularProgress, LinearProgress, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CHAT_TURN_REASSURANCE_AFTER_SECONDS } from "@/constants";
import type { PipelineVerboseStep } from "@/features/chat/lib/turnStream";
import { detailWithoutSqlSection } from "@/features/chat/lib/turnStream";
import { isCallTimingStep } from "@/features/chat/lib/turnTiming";
import { formatElapsed, phaseProgress, type TurnPhase } from "@/features/chat/lib/turnPhase";
import { useSqlDialect } from "@/shared/hooks/useSqlDialect";
import { SqlCodeMirror } from "@/shared/ui/SqlCodeMirror";

type ChatProcessingPanelProps = {
  currentVerboseStep: PipelineVerboseStep | null;
  verboseLog: PipelineVerboseStep[];
  domainId?: number | null;
  /** Rendered inside the answer's Processing tab: no card of its own around it. */
  embedded?: boolean;
  /** The reader-facing stage the turn is in; null until the first event arrives. */
  phase?: TurnPhase | null;
  /** Seconds since the question was sent. */
  elapsedSeconds?: number;
};

export function ChatProcessingPanel({
  currentVerboseStep,
  verboseLog,
  domainId,
  embedded = false,
  phase = null,
  elapsedSeconds = 0,
}: ChatProcessingPanelProps) {
  const { t } = useTranslation("chat");
  const [expanded, setExpanded] = useState(false);
  const logEndRef = useRef<HTMLDivElement>(null);
  const autoExpandedRef = useRef(false);
  const dialect = useSqlDialect(domainId);
  // Before the first event there is no phase to name: the generic title stands in.
  const phaseLabel = phase ? t(`phases.${phase}`) : t("processingTitle");
  const verboseHeading = currentVerboseStep?.summary ?? t("processingWaiting");
  const hasSql = verboseLog.some((step) => Boolean(step.sql));
  const showReassurance = elapsedSeconds >= CHAT_TURN_REASSURANCE_AFTER_SECONDS;

  // The query now streams in before it executes, and on a slow query that wait is the
  // whole point of showing it -- so open the log the first time SQL lands rather than
  // leaving it behind a collapsed header. Once only: a user who collapses it afterwards
  // has said what they want, and a later step must not reopen it.
  useEffect(() => {
    if (hasSql && !autoExpandedRef.current) {
      autoExpandedRef.current = true;
      setExpanded(true);
    }
  }, [hasSql]);

  useEffect(() => {
    if (
      expanded &&
      logEndRef.current &&
      typeof logEndRef.current.scrollIntoView === "function"
    ) {
      logEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [expanded, verboseLog.length]);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        width: "100%",
      }}
    >
      <Box
        sx={{
          width: "100%",
          // Same width as the answer that replaces this panel, so the column does not
          // jump when the turn finishes. The log carries whole SQL statements and raw
          // LLM payloads -- at 640px those wrapped into an unreadable ribbon.
          maxWidth: "100%",
          ...(embedded
            ? {}
            : {
                borderRadius: 2,
                border: "1px solid var(--theme-border)",
                bgcolor: "var(--theme-surface)",
                px: 2,
                py: 1.75,
              }),
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 1.5,
            mb: 1.25,
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "var(--theme-text-primary)" }}
          >
            {phaseLabel}
          </Typography>
          <Typography
            variant="caption"
            aria-label={t("processing.elapsedAria")}
            sx={{
              color: "var(--theme-text-secondary)",
              fontVariantNumeric: "tabular-nums",
              flexShrink: 0,
            }}
          >
            {formatElapsed(elapsedSeconds)}
          </Typography>
        </Box>
        {/* Progress by stage reached, not by time: the bar never pretends to know how long
            the warehouse or the model will take, and it never runs backwards. */}
        <LinearProgress
          data-testid="turn-phase-progress"
          variant={phase ? "determinate" : "indeterminate"}
          value={phaseProgress(phase)}
          sx={{
            mb: 1.5,
            height: 3,
            borderRadius: 999,
            bgcolor: "var(--theme-overlay-light-10)",
            "& .MuiLinearProgress-bar": {
              bgcolor: "var(--theme-text-secondary)",
            },
          }}
        />
        {showReassurance ? (
          <Typography
            variant="caption"
            role="status"
            sx={{ display: "block", color: "var(--theme-text-secondary)", mb: 1.25 }}
          >
            {t("processing.reassurance")}
          </Typography>
        ) : null}
        <Box
          sx={{
            borderRadius: 1.5,
            bgcolor: "var(--theme-background)",
            border: "1px solid var(--theme-border)",
            overflow: "hidden",
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={() => setExpanded((value) => !value)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              width: "100%",
              border: "none",
              bgcolor: "transparent",
              px: 1.5,
              py: 1,
              minHeight: 40,
              cursor: "pointer",
              textAlign: "left",
            }}
            aria-expanded={expanded}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "var(--theme-primary-light, var(--theme-primary))",
                  fontWeight: 600,
                }}
              >
                {verboseHeading}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
              <CircularProgress size={16} sx={{ color: "var(--theme-primary)" }} />
              {expanded ? (
                <KeyboardArrowUpIcon
                  sx={{ fontSize: 18, color: "var(--theme-text-secondary)" }}
                  aria-hidden
                />
              ) : (
                <KeyboardArrowDownIcon
                  sx={{ fontSize: 18, color: "var(--theme-text-secondary)" }}
                  aria-hidden
                />
              )}
            </Box>
          </Box>
          {expanded ? (
            <Box
              sx={{
                px: 1.5,
                pb: 1.5,
                maxHeight: 480,
                overflowY: "auto",
                borderTop: "1px solid var(--theme-border)",
              }}
            >
              {verboseLog.length === 0 ? (
                <Typography variant="body2" sx={{ color: "var(--theme-text-secondary)", pt: 1 }}>
                  {t("processingWaiting")}
                </Typography>
              ) : (
                // Timing-only LLM calls show up in the time breakdown, not in this log.
                verboseLog.filter((step) => !isCallTimingStep(step)).map((step) => {
                  // The payload copy is the untrimmed one, so when it is present the
                  // inline `SQL:` block in the detail body is dropped in favour of it.
                  const detail = step.sql
                    ? detailWithoutSqlSection(step.detail)
                    : step.detail;
                  return (
                    <Box key={`${step.stepIndex}-${step.eventType}`} sx={{ pt: 1.25 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "var(--theme-text-primary)" }}
                        >
                          {step.stepTitle}
                        </Typography>
                        {typeof step.durationMs === "number" ? (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "var(--theme-text-secondary)",
                              fontVariantNumeric: "tabular-nums",
                              flexShrink: 0,
                            }}
                          >
                            {formatElapsed(step.durationMs / 1000)}
                          </Typography>
                        ) : null}
                      </Box>
                      {detail ? (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "var(--theme-text-secondary)",
                            whiteSpace: "pre-wrap",
                            mt: 0.5,
                          }}
                        >
                          {detail}
                        </Typography>
                      ) : null}
                      {step.sql ? (
                        <Box sx={{ mt: 1 }}>
                          <SqlCodeMirror
                            value={step.sql}
                            readOnly
                            maxHeight="320px"
                            dialect={dialect}
                          />
                        </Box>
                      ) : null}
                    </Box>
                  );
                })
              )}
              <Box ref={logEndRef} />
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
